import { useEffect, useState } from 'react'
import { getSupabaseClient } from '../lib/supabase'

interface WidgetPreset {
  id: string
  primary: string
  secondary: string
  accent: string
}

interface SettingsRow {
  key: string
  value: unknown
}

interface WidgetStyleHook {
  colors: WidgetPreset
  loading: boolean
}

const DEFAULT_PRESET: WidgetPreset = {
  id: 'luxury_navy_gold',
  primary: '#1a1f3a',
  secondary: '#d4af37',
  accent: '#f8f9fa',
}

let cachedColors: WidgetPreset | null = null
let fetchPromise: Promise<WidgetPreset> | null = null

const hexToRgbTriplet = (hex: string): string | null => {
  const sanitized = hex.replace('#', '')
  const fullHex =
    sanitized.length === 3
      ? sanitized
          .split('')
          .map((char) => char + char)
          .join('')
      : sanitized

  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) {
    return null
  }

  const bigint = parseInt(fullHex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `${r} ${g} ${b}`
}

const applyCssVariables = (colors: WidgetPreset) => {
  console.log('🎨 [WIDGET] Applying CSS variables:', colors)
  const root = document.documentElement
  root.style.setProperty('--widget-primary', colors.primary)
  root.style.setProperty('--widget-secondary', colors.secondary)
  root.style.setProperty('--widget-accent', colors.accent)

  const primaryRgb = hexToRgbTriplet(colors.primary) ?? hexToRgbTriplet(DEFAULT_PRESET.primary)!
  const secondaryRgb = hexToRgbTriplet(colors.secondary) ?? hexToRgbTriplet(DEFAULT_PRESET.secondary)!
  const accentRgb = hexToRgbTriplet(colors.accent) ?? hexToRgbTriplet(DEFAULT_PRESET.accent)!

  root.style.setProperty('--widget-primary-rgb', primaryRgb)
  root.style.setProperty('--widget-secondary-rgb', secondaryRgb)
  root.style.setProperty('--widget-accent-rgb', accentRgb)
  console.log('✅ [WIDGET] CSS variables applied to DOM')
}

const isValidPreset = (preset: unknown): preset is { id?: string; primary: string; secondary: string; accent: string } => {
  return (
    !!preset &&
    typeof preset === 'object' &&
    typeof (preset as Record<string, unknown>).primary === 'string' &&
    typeof (preset as Record<string, unknown>).secondary === 'string' &&
    typeof (preset as Record<string, unknown>).accent === 'string'
  )
}

const fetchWidgetStyle = async (): Promise<WidgetPreset> => {
  console.log('🎨 [WIDGET] Fetching widget style from Supabase...')
  if (cachedColors) {
    console.log('✅ [WIDGET] Returning colors:', {
      primary: cachedColors.primary,
      secondary: cachedColors.secondary,
      accent: cachedColors.accent,
    })
    return cachedColors
  }

  if (!fetchPromise) {
    fetchPromise = (async () => {
      try {
        const supabase = getSupabaseClient()
        const { data, error } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', ['widget_active_preset', 'widget_presets'])
        console.log('📊 [WIDGET] Supabase response:', { data, error })

        if (error) {
          throw error
        }

        const rows = Array.isArray(data) ? (data as SettingsRow[]) : []

        const activePresetRow = rows.find((row) => row.key === 'widget_active_preset')
        const presetsRow = rows.find((row) => row.key === 'widget_presets')

        const activePresetId =
          typeof activePresetRow?.value === 'string' && activePresetRow.value.trim().length > 0
            ? activePresetRow.value
            : DEFAULT_PRESET.id
        console.log('🎯 [WIDGET] Active preset ID:', activePresetId)

        let presetsPayload: unknown = presetsRow?.value
        if (typeof presetsPayload === 'string') {
          try {
            presetsPayload = JSON.parse(presetsPayload)
          } catch (parseError) {
            console.error('Failed to parse widget presets payload', parseError)
            presetsPayload = null
          }
        }

        const presetMap =
          presetsPayload && typeof presetsPayload === 'object'
            ? (presetsPayload as Record<string, unknown>)
            : {}
        console.log('📦 [WIDGET] All available presets:', Object.keys(presetMap))

        const activePresetCandidate = presetMap[activePresetId]
        console.log('🔍 [WIDGET] Found preset for ID:', activePresetId, '→', activePresetCandidate)

        if (isValidPreset(activePresetCandidate)) {
          console.log('✅ [WIDGET] Returning colors:', {
            primary: activePresetCandidate.primary,
            secondary: activePresetCandidate.secondary,
            accent: activePresetCandidate.accent,
          })
          return {
            id: typeof activePresetCandidate.id === 'string' ? activePresetCandidate.id : activePresetId,
            primary: activePresetCandidate.primary,
            secondary: activePresetCandidate.secondary,
            accent: activePresetCandidate.accent,
          }
        }

        const defaultPresetCandidate = Object.values(presetMap).find(
          (preset) => isValidPreset(preset) && !!(preset as { isDefault?: boolean }).isDefault,
        )

        if (isValidPreset(defaultPresetCandidate)) {
          console.log('✅ [WIDGET] Returning colors:', {
            primary: defaultPresetCandidate.primary,
            secondary: defaultPresetCandidate.secondary,
            accent: defaultPresetCandidate.accent,
          })
          return {
            id: typeof defaultPresetCandidate.id === 'string' ? defaultPresetCandidate.id : DEFAULT_PRESET.id,
            primary: defaultPresetCandidate.primary,
            secondary: defaultPresetCandidate.secondary,
            accent: defaultPresetCandidate.accent,
          }
        }

        console.log('✅ [WIDGET] Returning colors:', {
          primary: DEFAULT_PRESET.primary,
          secondary: DEFAULT_PRESET.secondary,
          accent: DEFAULT_PRESET.accent,
        })
        return DEFAULT_PRESET
      } catch (error) {
        console.log('❌ [WIDGET] Error loading presets:', error)
        console.error('Failed to load widget style presets from Supabase', error)
        console.log('✅ [WIDGET] Returning colors:', {
          primary: DEFAULT_PRESET.primary,
          secondary: DEFAULT_PRESET.secondary,
          accent: DEFAULT_PRESET.accent,
        })
        return DEFAULT_PRESET
      }
    })().finally(() => {
      fetchPromise = null
    })
  }

  cachedColors = await fetchPromise
  return cachedColors
}

export const useWidgetStyle = (): WidgetStyleHook => {
  const [colors, setColors] = useState<WidgetPreset>(cachedColors ?? DEFAULT_PRESET)
  const [loading, setLoading] = useState<boolean>(!cachedColors)

  useEffect(() => {
    console.log('🔄 [WIDGET] useWidgetStyle hook mounted')
    let isMounted = true

    const load = async () => {
      console.log('⏳ [WIDGET] Loading preset data...')
      const resolved = await fetchWidgetStyle()
      console.log('✅ [WIDGET] Preset data loaded, applying to state')
      if (!isMounted) return
      cachedColors = resolved
      setColors(resolved)
      setLoading(false)
    }

    load()

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    applyCssVariables(colors)
  }, [colors])

  return { colors, loading }
}

